import { mostReadable } from '@eigtech/ui-shared-color'
import { toTinyColor } from './normalizeColor'

/**
 * Tint/shade percentages per stop, reproducing the maketintsandshades.com output
 * dave's `brandPrimary` was originally generated with - every one of its 11 stops
 * comes back identical. Note the scale runs to 1000 rather than the usual 900,
 * because dave's theme reads `brandPrimary.1000`.
 *
 * Dave's `brandSecondary` was generated with a different tint ladder
 * (90/70/50/30/10 instead of 90/80/60/40/20; its shades match), so stops 100-400 of
 * a generated secondary scale come out slightly lighter than the hardcoded ones.
 * One ladder is used for every scale here rather than carrying that inconsistency
 * forward - `brandPrimary` is the scale that drives chakra's default colorScheme.
 */
const SCALE_STEPS: Record<number, { tint?: number; shade?: number }> = {
  50: { tint: 90 },
  100: { tint: 80 },
  200: { tint: 60 },
  300: { tint: 40 },
  400: { tint: 20 },
  500: {},
  600: { shade: 10 },
  700: { shade: 20 },
  800: { shade: 30 },
  900: { shade: 40 },
  1000: { shade: 50 },
}

export type ColorScale = Record<number, string>

/**
 * Expand a single color into a chakra-style scale.
 *
 * @returns undefined when the color cannot be parsed, so callers can fall back to
 * whatever the theme already defines
 */
export function generateColorScale(color: string | undefined): ColorScale | undefined {
  const base = toTinyColor(color)
  if (!base) return undefined

  return Object.fromEntries(
    Object.entries(SCALE_STEPS).map(([stop, { tint, shade }]) => {
      const value = base.clone()

      return [
        stop,
        (tint !== undefined
          ? value.tint(tint)
          : shade !== undefined
            ? value.shade(shade)
            : value
        ).toHexString(),
      ]
    })
  ) as ColorScale
}

/**
 * Resolve the foreground to pair with a background color. The white label theme's
 * foreground fields are optional and are not seeded today, so fall back to whichever
 * of black/white reads better against the background.
 */
export function resolveForeground(
  background: string | undefined,
  explicit: string | undefined
): string | undefined {
  if (explicit) return explicit

  const parsed = toTinyColor(background)
  if (!parsed) return undefined

  return mostReadable(parsed, ['#fff', '#000'])?.toHexString()
}
